import { createFileRoute, Link } from "@tanstack/react-router";
import { Container, CtaBand, Eyebrow, Lead } from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { CASE_STUDIES } from "@/content/case-studies";
import { contactFunnel } from "@/lib/analytics";

const PATH = "/case-studies";
const CTA_SOURCE_BAND = "case_studies_hub_footer";

const title = "Case studies, technology leadership engagements";
const description =
  "Named engagements with measurable outcomes: disaster recovery halved in financial services, search monitoring scaled 1900%, a start-up stack from nothing.";

export const Route = createFileRoute("/case-studies/")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "case-studies",
        "Case studies, named outcomes from twenty years of engagements",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Case studies",
          url: `${SITE.url}${PATH}`,
          author: { "@type": "Person", name: SITE.person, url: SITE.linkedin },
          hasPart: CASE_STUDIES.map((c) => ({
            "@type": "Article",
            headline: c.title,
            url: `${SITE.url}${PATH}/${c.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            { "@type": "ListItem", position: 2, name: "Case studies", item: `${SITE.url}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: CaseStudiesIndexPage,
});

function CaseStudiesIndexPage() {
  return (
    <div>
      <section
        aria-labelledby="cs-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Case studies</Eyebrow>
          <h1
            id="cs-hero-heading"
            className="mt-4 max-w-[20ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Engagements I ran, and what changed.
          </h1>
          <Lead className="mt-6">
            These are engagements delivered as an operator inside the business, on the leadership
            team, accountable to the board. Not reports written from the outside. Clients are
            described by sector because the engagement records are confidential, and every figure
            below is one those records state.
          </Lead>
        </Container>
      </section>

      <section aria-labelledby="cs-list-heading" className="border-b border-border">
        <Container>
          <h2 id="cs-list-heading" className="sr-only">
            All case studies
          </h2>
          <ul className="grid gap-6 md:grid-cols-3">
            {CASE_STUDIES.map((c) => (
              <li
                key={c.slug}
                className="flex h-full flex-col rounded-lg border border-border bg-card p-6"
              >
                <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                  {c.sector}
                </p>
                <h3 className="mt-3 text-lg font-semibold">
                  <Link
                    to="/case-studies/$slug"
                    params={{ slug: c.slug }}
                    className="transition-colors hover:text-primary"
                  >
                    {c.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[15px] font-semibold text-primary">{c.headline}</p>
                <p className="mt-3 flex-1 text-[15px] leading-relaxed text-body">{c.summary}</p>
                <dl className="mt-5 space-y-1.5 text-sm text-muted-foreground">
                  <div className="flex gap-2">
                    <dt className="font-semibold text-ink">Role</dt>
                    <dd>{c.role}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-semibold text-ink">When</dt>
                    <dd>{c.era}</dd>
                  </div>
                </dl>
                <p className="mt-5">
                  <Link
                    to="/case-studies/$slug"
                    params={{ slug: c.slug }}
                    className="inline-flex items-center gap-1.5 rounded-sm text-[15px] font-semibold transition-colors hover:text-primary focus-visible:underline"
                  >
                    Read the case study <span aria-hidden="true">&rarr;</span>
                  </Link>
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <CtaBand
        eyebrow="Next step"
        heading="Recognise your own situation in one of these?"
        body="If any of this looks like the position you are in, a short conversation is usually enough to work out whether I can help and how."
        to="/contact"
        cta="Start a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
